import { SchoolSearchService } from "@/services/SchoolSearch";
import { SchoolSearchTrackingService } from "@/services/SchoolSearch/SchoolSearchTrackingService";
import userData from "@/services/clientUserData";
import cloneDeep from "lodash/cloneDeep";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isPlainObject from "lodash/isPlainObject";
import clientData from '@/services/clientData'

const service = new SchoolSearchService({
    gender: userData.gender,
    siteId: userData.siteId,
    userId: userData.userId,
    rosterNeedsFiltersEnabled: clientData('rosterNeedsFiltersEnabled')
});

export async function boot({ commit }) {
    const { awsS3Url, schools, filterOptions, positions, needsBySchool } = await service.boot();

    commit('SET_S3_URL', awsS3Url);
    commit('SET_SCHOOLS', schools);
    commit('SET_FILTER_OPTIONS', filterOptions);
    commit('SET_IS_BOOTED', true);
    commit('SET_SPORT_POSITIONS_MAP', positions);
    commit('SET_NEEDS_BY_SCHOOL', needsBySchool);
}

export async function filter({ state, getters, commit, dispatch }) {
    commit('SET_OPEN_FILTER', null);
    commit('START_LOADING');
    commit('SELECT_SCHOOL', null);
    commit('REMOVE_FILTER', state.activeLocationFilter === "region" ? "location.distance" : "location.regions");

    const filterParams = removeEmptyFilters(cloneDeep(state.preAppliedFilters));
    commit('SET_APPLIED_FILTERS', filterParams);
    setQueryParamFilters(filterParams)

    try {
        await dispatch('validateLocationFilter')
        const schools = await service.filter(filterParams);
        commit('SET_SCHOOLS', schools);
        commit('STOP_APPLYING_QUERY_PARAM_FILTERS');
        return await service.trackInDb(filterParams, schools.length);
    } finally {
        commit('STOP_LOADING');
    }
}

export async function validateLocationFilter({ state, getters, commit, dispatch }, retryCount = 0, maxRetries = 10){
    if (state.preAppliedFilters.location?.distance && state.preAppliedFilters.location?.distance?.zipcode) {
        if (state.preAppliedFilters.location.distance.location?.lat && state.preAppliedFilters.location.distance.location?.lng) {
            return true;
        }

        if (retryCount >= maxRetries) {
            throw new Error('Timeout waiting for location data');
        }

        await new Promise(resolve => setTimeout(resolve, 200));

        return dispatch('validateLocationFilter', retryCount + 1, maxRetries);
    } else if ((state.preAppliedFilters.location?.regions || state.preAppliedFilters.location)  && state.preAppliedFilters.location?.regions?.regions) {
        if (state.preAppliedFilters.location.regions.states) {
            return true;
        }

        if (retryCount >= maxRetries) {
            throw new Error('Timeout waiting for states data');
        }

        await new Promise(resolve => setTimeout(resolve, 200));

        return dispatch('validateLocationFilter', retryCount + 1, maxRetries);
    }
    return true;
}

function setQueryParamFilters(filterParams) {
    const queryParams = Object.entries(filterParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(JSON.stringify(value))}`)
      .join('&');
    const currentUrl = window.location.href;
    const urlParts = currentUrl.split('?');
    const baseUrl = urlParts[0];
    window.history.replaceState(null, '', `${baseUrl}?${queryParams}`);
}

export function clearFilters({ commit, dispatch }) {
    commit('SET_APPLIED_FILTERS', {});
    dispatch('filter');
}

export function openFilter({ commit }, filterPath) {
    commit('SET_OPEN_FILTER', filterPath);
}

export function closeFilter({ commit, state }, filterPath) {
    if (state.openFilter !== filterPath) {
        return // that filter is not currently open
    }

    commit('SET_OPEN_FILTER', null);
    commit('RESET_FILTERS', state.appliedFilters);
}

export function selectSchool({ commit }, school) {
    // let all the clickaway events to run first.
    setTimeout(() => commit('SELECT_SCHOOL', school), 0);
}

export function clearSchoolSelection({ commit }) {
    commit('SELECT_SCHOOL', null)
}

export function setFilterValue({ commit }, { filterPath, value }) {
    if (isFilterEmpty(value)) {
        commit('REMOVE_FILTER', filterPath);
    } else {
        commit('ADD_FILTER', {
            name: filterPath,
            value: cloneDeep(value),
        });
    }
}

export function setActiveLocationFilter({ commit }, filter) {
    commit('SET_ACTIVE_LOCATION_FILTER', filter);
}

export function toggleSchoolSearchModal({ commit }, modalData) {
    commit('TOGGLE_SCHOOL_SEARCH_MODAL', modalData);
}

function removeEmptyFilters(filters) {
    for (const key in filters) {
        if (Object.hasOwnProperty.call(filters, key)) {
            const filter = filters[key];

            if (isPlainObject(filter)) {
                removeEmptyFilters(filter);
            }

            if (isFilterEmpty(filter)) {
                delete filters[key];
            }
        }
    }
    return filters;
}

function isFilterEmpty(filter) {
    if (isPlainObject(filter) || isArray(filter)) {
        return isEmpty(filter);
    }

    return filter === undefined || filter === null;
}

export async function segmentTrack(_, { eventName, eventData }) {
    await service.segmentTrack(eventName, eventData);
}